import React, { useState, useEffect } from 'react';
import { Navigation, Calendar, User, ArrowRight, Search, Play, Smartphone } from 'lucide-react';

const TEAL = '#00A878';
const DARK = '#0D1117';
const WHITE = '#FFFFFF';
const MUTED = '#6c757d';

const API = 'https://blog.spotfinder.co.ke/wp-json/wp/v2/posts?_embed&per_page=12';

function ImgPlaceholder({ height = '200px', text, radius = '12px' }) {
  return (
    <div style={{
      width: '100%', height,
      background: 'linear-gradient(135deg, #E8F5F1, #B2DFDB)',
      borderRadius: radius,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <Navigation size={28} color={TEAL} aria-hidden="true" />
      <div style={{ fontSize: '12px', fontWeight: '600', marginTop: '8px', color: TEAL }}>{text}</div>
    </div>
  );
}

function stripHtml(html) {
  return html.replace(/<[^>]*>/g, '');
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-KE', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch(API)
      .then(res => res.json())
      .then(data => { setPosts(data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const filtered = posts.filter(p =>
    p.title.rendered.toLowerCase().includes(search.toLowerCase()) ||
    stripHtml(p.excerpt.rendered).toLowerCase().includes(search.toLowerCase())
  );

  // ── SINGLE POST VIEW ──
  if (selected) {
    return (
      <div style={{ background: WHITE }}>
        <div className="blog-hero-pad" style={{ background: WHITE, padding: '60px 0' }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">

                <button
                  onClick={() => setSelected(null)}
                  aria-label="Back to blog"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: TEAL, fontWeight: '700', fontSize: '14px',
                    padding: 0, marginBottom: '32px',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                  ← Back to Blog
                </button>

                {selected._embedded?.['wp:featuredmedia']?.[0]?.source_url ? (
                  <img
                    className="blog-post-img"
                    src={selected._embedded['wp:featuredmedia'][0].source_url}
                    alt={stripHtml(selected.title.rendered)}
                    style={{
                      width: '100%', borderRadius: '16px',
                      marginBottom: '40px', objectFit: 'cover', height: '380px',
                    }}
                  />
                ) : (
                  <div style={{ marginBottom: '40px' }}>
                    <ImgPlaceholder height="380px" text="Featured Image" radius="16px" />
                  </div>
                )}

                <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                  <div style={{
                    display: 'inline-block',
                    background: '#E8F5F1', border: '1px solid #B2DFDB',
                    borderRadius: '20px', padding: '4px 14px',
                  }}>
                    <span style={{ color: TEAL, fontSize: '11px', fontWeight: '700', letterSpacing: '1px' }}>
                      PARKING INSIGHTS
                    </span>
                  </div>
                  <span style={{ color: MUTED, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Calendar size={13} aria-hidden="true" /> {formatDate(selected.date)}
                  </span>
                  <span style={{ color: MUTED, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <User size={13} aria-hidden="true" /> {selected._embedded?.author?.[0]?.name || 'Spot Finder'}
                  </span>
                </div>

                <h1
                  className="blog-post-title"
                  style={{
                    fontWeight: '900', fontSize: '2.8rem',
                    lineHeight: 1.15, letterSpacing: '-1px',
                    color: DARK, marginBottom: '32px',
                  }}
                  dangerouslySetInnerHTML={{ __html: selected.title.rendered }}
                />

                <div style={{ height: '1px', background: '#e9ecef', marginBottom: '32px' }} />

                <div
                  className="blog-post-content"
                  style={{ color: MUTED, fontSize: '1rem', lineHeight: 1.9 }}
                  dangerouslySetInnerHTML={{ __html: selected.content.rendered }}
                />

              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── BLOG LIST VIEW ──
  return (
    <div style={{ background: WHITE }}>

      {/* ── HERO ── */}
      <div className="blog-hero-pad" style={{ background: WHITE, padding: '80px 0 60px' }}>
        <div className="container">
          <div className="row align-items-start g-5 mb-5">
            <div className="col-lg-6">
              <div style={{
                display: 'inline-block',
                background: '#E8F5F1', border: '1px solid #B2DFDB',
                borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
              }}>
                <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
                  SPOT FINDER BLOG
                </span>
              </div>
              <h1 style={{
                fontWeight: '900', fontSize: '2.8rem',
                lineHeight: 1.15, letterSpacing: '-1px',
                color: DARK, marginTop: '0',
              }}>
                Parking tips and<br />
                <span style={{ color: '#adb5bd' }}>city insights</span>
              </h1>
            </div>
            <div className="col-lg-4 offset-lg-2 d-flex align-items-end" style={{ paddingBottom: '8px' }}>
              <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
                Stay up to date with the latest parking tips, Nairobi traffic insights, new spot announcements, and product updates from the Spot Finder team.
              </p>
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <input
              type="search"
              aria-label="Search blog articles"
              placeholder="Search articles..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="blog-search-input"
              style={{
                maxWidth: '420px', width: '100%',
                padding: '12px 18px', border: '1.5px solid #e9ecef',
                borderRadius: '10px', fontSize: '14px',
                color: DARK, outline: 'none', background: WHITE,
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = TEAL}
              onBlur={e => e.target.style.borderColor = '#e9ecef'}
            />
            <button
              aria-label="Search articles"
              style={{
                background: '#1B2A4A', color: WHITE,
                border: 'none', borderRadius: '10px',
                padding: '12px 24px', fontWeight: '700',
                fontSize: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
              <Search size={16} aria-hidden="true" /> Search
            </button>
          </div>
        </div>
      </div>

      {/* ── POSTS ── */}
      <div className="blog-posts-pad" style={{ background: '#F5F5F5', padding: '80px 0' }}>
        <div className="container">

          {loading && (
            <div className="text-center py-5" role="status" aria-live="polite">
              <div style={{ color: MUTED, fontSize: '1rem' }}>Loading articles...</div>
            </div>
          )}

          {error && (
            <div className="text-center py-5" role="alert">
              <div style={{ color: '#DC2626', fontSize: '1rem', fontWeight: '600' }}>
                Could not load posts. Please check your API connection.
              </div>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-5">
              <div style={{ color: MUTED, fontSize: '1rem' }}>
                No articles found for "{search}"
              </div>
            </div>
          )}

          <div className="row g-4">
            {filtered.map((post) => {
              const featuredImg = post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
              const excerpt = stripHtml(post.excerpt.rendered).slice(0, 120) + '...';
              const author = post._embedded?.author?.[0]?.name || 'Spot Finder';

              return (
                <div className="col-md-4" key={post.id}>
                  <article
                    style={{
                      background: WHITE, borderRadius: '16px',
                      padding: '10px', cursor: 'pointer',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                      border: '1px solid #f0f0f0',
                      transition: 'box-shadow 0.2s, transform 0.2s',
                      height: '100%', display: 'flex', flexDirection: 'column',
                    }}
                    onClick={() => setSelected(post)}
                    onKeyDown={e => e.key === 'Enter' && setSelected(post)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Read article: ${stripHtml(post.title.rendered)}`}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}>

                    <div className="blog-card-img" style={{
                      borderRadius: '8px', overflow: 'hidden',
                      height: '180px', flexShrink: 0,
                    }}>
                      {featuredImg ? (
                        <img
                          src={featuredImg}
                          alt={stripHtml(post.title.rendered)}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <ImgPlaceholder height="100%" text="Blog Image" radius="8px" />
                      )}
                    </div>

                    <div style={{ padding: '16px 16px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>

                      <div style={{
                        display: 'inline-block',
                        background: '#E8F5F1', border: '1px solid #B2DFDB',
                        borderRadius: '20px', padding: '3px 12px',
                        marginBottom: '12px', alignSelf: 'flex-start',
                      }}>
                        <span style={{ color: TEAL, fontSize: '11px', fontWeight: '700', letterSpacing: '1px' }}>
                          PARKING INSIGHTS
                        </span>
                      </div>

                      <h2 style={{
                        fontWeight: '800', color: DARK,
                        fontSize: '1.05rem', lineHeight: 1.4, marginBottom: '10px',
                      }}
                        dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                      />

                      <p style={{ color: MUTED, fontSize: '0.88rem', lineHeight: 1.7, marginBottom: '16px', flex: 1 }}>
                        {excerpt}
                      </p>

                      <div style={{
                        borderTop: '1px solid #f0f0f0', paddingTop: '14px',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '30px', height: '30px', borderRadius: '50%',
                            background: 'linear-gradient(135deg, #E8F5F1, #B2DFDB)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <User size={13} color={TEAL} aria-hidden="true" />
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: DARK }}>{author}</div>
                            <div style={{ fontSize: '11px', color: MUTED }}>{formatDate(post.date)}</div>
                          </div>
                        </div>
                        <div style={{ color: TEAL, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '700' }}>
                          Read <ArrowRight size={14} aria-hidden="true" />
                        </div>
                      </div>

                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="cta-outer" style={{ background: '#F5F5F5', padding: '40px 24px 100px' }}>
        <div className="cta-inner" style={{
          background: TEAL, borderRadius: '20px',
          padding: '60px 60px 0', overflow: 'visible', position: 'relative',
        }}>
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0, opacity: 0.06,
            backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
            backgroundSize: '24px 24px', borderRadius: '20px', pointerEvents: 'none',
          }} />
          <div className="row align-items-end" style={{ position: 'relative', zIndex: 1 }}>
            <div className="col-lg-7" style={{ paddingBottom: '60px' }}>
              <h2 style={{ fontWeight: '900', fontSize: '2.8rem', color: WHITE, lineHeight: 1.15, letterSpacing: '-1px', marginBottom: '16px' }}>
                Ready to park smarter<br />across Nairobi?
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '32px' }}>
                Download the app or use the web version to start finding and reserving parking spots instantly.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <a href="https://play.google.com/store/apps/details?id=com.spotfinder.owner&hl=en"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on Google Play"
                  style={{
                    background: DARK, color: WHITE, border: 'none',
                    borderRadius: '10px', padding: '12px 22px',
                    display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none',
                  }}>
                  <Play size={22} fill={WHITE} color={WHITE} aria-hidden="true" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>GET IT ON</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1.3 }}>Google Play</div>
                  </div>
                </a>
                <a href="https://apps.apple.com/ke/app/spotfinder-parking-finder/id6572302355"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on App Store"
                  style={{
                    background: DARK, color: WHITE, border: 'none',
                    borderRadius: '10px', padding: '12px 22px',
                    display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none',
                  }}>
                  <Smartphone size={22} color={WHITE} aria-hidden="true" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>Download on the</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1.3 }}>App Store</div>
                  </div>
                </a>
              </div>
            </div>
            <div className="col-lg-5 d-flex justify-content-center align-items-end">
              <img
                className="cta-phone"
                src="/images/spotfinder-screen.webp"
                alt="Spot Finder app screen showing parking reservations"
                style={{
                  width: '480px', display: 'block',
                  marginBottom: '-80px', marginTop: '-40px',
                  filter: 'drop-shadow(0 32px 64px rgba(0,0,0,0.35))',
                  position: 'relative', zIndex: 2,
                }}
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}